import { zodResolver } from "@hookform/resolvers/zod";
import { ComponentProps, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import z from "zod";
import { enqueueSnackbar } from "../../../../components/GlobalSnackbar/snackbarStore";
import { isSupabaseError } from "../../../../constants/supabaseErrors";
import emptyHtmlToNull from "../../../../utils/emptyHtmlToNull";
import { type ReminderRecurringType } from "../../../../types";
import usePostReminder from "../../api/usePostReminder";
import useUpdateReminder from "../../api/useUpdateReminder";
import AddUpdateReminder from "./AddUpdateReminder";

const formSchema = z.object({
  title: z.string().trim().min(1, "Ce champ est obligatoire"),
  description: z.string().optional(),
  due_date: z.date().refine((date) => !isNaN(date.getTime()), {
    message: "Format de date invalide",
  }).transform((date) => date.toISOString()),
  provider_id: z.string().nullable(),
  subject_id: z.string().nullable(),
  is_recurring: z.enum(["none", "recurring"]),
  recurrence_value: z.number().nullable(),
  recurrence_type: z.custom<ReminderRecurringType>(),
})
  .refine((data) => data.provider_id || data.subject_id, {
    message: "Au moins un prestataire ou un sujet doit être sélectionné",
    path: ["root"],
  })
  .transform((data) => {
    const { is_recurring, ...rest } = data;

    return {
      ...rest,
      description: emptyHtmlToNull(data.description),
      recurrence_type: is_recurring === "recurring" ? data.recurrence_type : "none",
      recurrence_value: is_recurring === "recurring" ? data.recurrence_value : null,
    }
  });

type FormInputs = z.input<typeof formSchema>;
type FormValues = z.infer<typeof formSchema>;

export default function useAddUpdateReminder(props: ComponentProps<typeof AddUpdateReminder>) {
  const navigate = useNavigate();
  const postReminder = usePostReminder();
  const updateReminder = useUpdateReminder();

  const isRecurring = props.reminder?.recurrence_type
    ? props.reminder.recurrence_type !== "none"
    : false;

  const form = useForm<FormInputs, unknown, FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: props.reminder?.title || "",
      description: props.reminder?.description || "",
      due_date: props.reminder?.due_date ? new Date(props.reminder.due_date) : new Date(),
      provider_id: props.reminder?.provider_id || props.providerId || null,
      is_recurring: isRecurring ? "recurring" : "none",
      recurrence_value: props.reminder?.recurrence_value || 1,
      recurrence_type: props.reminder?.recurrence_type || "yearly",
      subject_id: props.reminder?.subject_id || props.subjectId || null,
    },
  });

  const [showDescription, setShowDescription] = useState(
    !!props.reminder?.description
  );

  function handleAddRecurrence() {
    form.setValue("is_recurring", "recurring");
  }

  function handleRemoveRecurrence() {
    form.setValue("is_recurring", "none");
  }

  function handleAddDescription() {
    setShowDescription(true);
  }

  const handleConfirm = async (data: FormValues) => {
    try {
      if (props.reminder) {
        await updateReminder.mutateAsync({
          reminderId: props.reminder.id,
          command: data,
        });
        enqueueSnackbar("Rappel mis à jour", "success");
      } else {
        await postReminder.mutateAsync(data);
        enqueueSnackbar("Rappel créé", "success");

        if (props.navigatePath) {
          navigate(props.navigatePath);
        }
      }

      props.afterSuccess?.();
      props.onClose();
    } catch (error) {
      if (isSupabaseError(error)) {
        enqueueSnackbar(error.message, "error");
      } else {
        enqueueSnackbar("Une erreur est survenue", "error");
      }
    }
  };

  const isSubmitting = postReminder.isPending || updateReminder.isPending;

  return {
    form,
    handleConfirm: form.handleSubmit(handleConfirm),
    isSubmitting,
    showDescription,
    handleAddRecurrence,
    handleRemoveRecurrence,
    handleAddDescription,
    serverError: (postReminder.error || updateReminder.error) ? (postReminder.error?.message || updateReminder.error?.message || 'Une erreur est survenue') : null,
  };
}
